import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FormattedMessage } from 'react-intl-next';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingButton } from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { ButtonItem, HeadingItem, MenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';
import { Inline } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';

import {
  MOCK_IR_DIFF_FETCH,
  REFETCH_IR_DIFF,
  publishPullRequestTrackEvent,
} from 'src/redux/pull-request/actions';
import {
  getIsCurrentPullRequestOpen,
  getPullRequestDestinationHash,
  getPullRequestSourceHash,
  getIsIterativeReviewComplete,
  getIRAnalyticsTrackingAttributes,
  getIsStickyHeaderActive,
  getPullRequestRefetchIRDiffDummy,
} from 'src/redux/pull-request/selectors';
import { prCommitActions } from 'src/redux/repo-commits/actions';
import { getRepoCommitsState } from 'src/redux/repo-commits/selectors';
import { useHistory__UNSAFE, useLocation, useParams } from 'src/router/hooks';
import urls from 'src/urls';
import {
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';
import { hashCompare, short12Hash, shortHash } from 'src/utils/short-hash';

import { buttonAppearance, buttonSpacing } from '../constants';
import {
  DiffStateContext,
  MultiCommitSelection,
  SelectedDiffType,
  defaultMultiCommitSelection,
} from '../tabs/diff/diff-state-context';
import { getDiffHashes } from '../tabs/diff/utils';

import { CommitItemsList } from './commit-items-list';
import { messages } from './iterative-review.i18n';
import * as styles from './styles';
import { getCommitMessage, getLastReviewedCommit } from './utils';

type IterativeReviewSelectorProps = {
  isIterativeReviewSelectorOpen: boolean;
  setIsIterativeReviewSelectorOpen: Dispatch<SetStateAction<boolean>>;
  isStickyHeader?: boolean;
};

export const IterativeReviewSelector = ({
  isIterativeReviewSelectorOpen,
  setIsIterativeReviewSelectorOpen,
  isStickyHeader = false,
}: IterativeReviewSelectorProps): JSX.Element => {
  const dispatch = useDispatch();
  const {
    iterativeReviewSelectedDiff,
    setIterativeReviewSelectedDiff,
    activeIndex,
    setActiveIndex,
    multiCommitSelection,
    setMultiCommitSelection,
    commitCSLRHash,
    setCommitCSLRHash,
  } = useContext(DiffStateContext);

  // If this is not being rendered in the sticky header,
  // we need to close the selector when the sticky header is activated.
  // This prevents the scrolling bug found in COREX-8483.
  const isStickyHeaderActive = useSelector(getIsStickyHeaderActive);
  useEffect(() => {
    if (isStickyHeaderActive && !isStickyHeader) {
      setIsIterativeReviewSelectorOpen(false);
    }
  }, [isStickyHeaderActive, isStickyHeader, setIsIterativeReviewSelectorOpen]);

  const {
    commits,
    isLoading: isCommitsLoading,
    nextUrl: nextCommitsUrl,
  } = useSelector(getRepoCommitsState);
  const prOpen = useSelector(getIsCurrentPullRequestOpen);
  const prAnchorHash = useSelector(getPullRequestSourceHash) || '';
  const prDestinationHash = useSelector(getPullRequestDestinationHash) || '';
  const isIterativeReviewCompleted = useSelector(getIsIterativeReviewComplete);
  // History is intentionally untyped (see function declaration). To replace the history and not
  // stack URLs the user would have to hit back through, we use history.replace, so the back button
  // will still take the user back to the PR list page. This is used here, comment-preview.tsx, and
  // commit-item.tsx for iterative review purposes.
  const history = useHistory__UNSAFE();
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  const diffTabUrl = urls.ui.pullRequestTab(
    repositoryOwner,
    repositorySlug,
    pullRequestId,
    'diff'
  );

  const tabIndex = isStickyHeader ? -1 : 0;

  const path = useLocation().pathname;

  const prCommits = useMemo(() => commits || [], [commits]);

  const cslrHashIndex = prCommits.findIndex(
    commit => short12Hash(commit.hash) === commitCSLRHash
  );

  const mergeCommitFound =
    cslrHashIndex > -1
      ? prCommits
          .slice(0, cslrHashIndex)
          .some(commit => commit.parents && commit.parents.length >= 2)
      : false;

  const commitCount = prCommits.length;

  // If the hash index is -1, then the hash is no longer in the commit history,
  // such as when a rebase occurs. In this case, we don't want to show CSLR as the chosen
  // option.
  const commitCSLRCount =
    commitCSLRHash && cslrHashIndex > -1 ? cslrHashIndex : 0;

  const irTracking = useSelector(getIRAnalyticsTrackingAttributes);
  irTracking.irSelectedDiffType =
    SelectedDiffType[iterativeReviewSelectedDiff.selectedDiffType];

  const [initialMultiSelectState, setInitialMultiSelectState] =
    useState<MultiCommitSelection>(defaultMultiCommitSelection);

  const [tooltipContent, setTooltipContent] = useState<string>('');

  // serves to fetch the diff on initial page load
  useEffect(() => {
    if (
      !isCommitsLoading &&
      iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.None
    ) {
      // TODO: highlight the selected commits in the custom range : see COREX-7781
      const [specSrc, specDest] = getDiffHashes(path);

      // The regex check for hashes didn't match, so exit early and view all changes
      if (!specSrc || !specDest) {
        setIterativeReviewSelectedDiff({
          sourceHash: prAnchorHash,
          destHash: prDestinationHash,
          isLoading: false,
          selectedDiffType: SelectedDiffType.All,
        });
        return;
      }

      // check if the specSrc and specDest are valid commits
      const specSrcIndex = prCommits.findIndex(commit =>
        hashCompare(commit.hash, specSrc)
      );
      const specDestIndex = prCommits.findIndex(commit =>
        hashCompare(commit.hash, specDest)
      );

      if (specSrcIndex > -1 && specDestIndex > -1) {
        const sourceCommit = prCommits[specSrcIndex];
        const destinationCommit = prCommits[specDestIndex];
        const destChildCommit = prCommits[specDestIndex - 1];
        const singleCommitDiff =
          destinationCommit.hash === sourceCommit.parents?.[0]?.hash;

        setIterativeReviewSelectedDiff({
          sourceHash: short12Hash(sourceCommit.hash),
          destHash: short12Hash(destinationCommit.hash),
          isLoading: false,
          selectedDiffType: singleCommitDiff
            ? SelectedDiffType.Single
            : SelectedDiffType.Custom,
        });
        setMultiCommitSelection({
          sourceCommit,
          sourceIndex: specSrcIndex,
          destinationCommit: destChildCommit,
          destinationIndex: specDestIndex - 1,
        });
      } else if (specSrcIndex > -1 && specDestIndex === -1) {
        // Sometimes a commit can have a parent that is not part of the pull request
        // or isn't loaded in the commit list for the last commit currently on the list,
        // We should check for that and not override to the all changes diff
        const sourceCommit = prCommits[specSrcIndex];
        const singleCommitDiff = hashCompare(
          specDest,
          sourceCommit.parents?.[0]?.hash || ''
        );
        // The dest hash might be the parent of a commit in the PR where the parent is
        // not a commit in the PR.
        const destIndex = prCommits.findIndex(commit =>
          hashCompare(specDest, commit.parents?.[0]?.hash || '')
        );
        if (singleCommitDiff) {
          setIterativeReviewSelectedDiff({
            sourceHash: short12Hash(sourceCommit.hash),
            destHash: short12Hash(sourceCommit.parents![0].hash),
            isLoading: false,
            selectedDiffType: SelectedDiffType.Single,
          });
          setMultiCommitSelection({
            sourceCommit,
            sourceIndex: specSrcIndex,
            destinationCommit: sourceCommit,
            destinationIndex: specSrcIndex,
          });
        } else if (destIndex > -1) {
          const destinationCommit = prCommits[destIndex];
          setIterativeReviewSelectedDiff({
            sourceHash: short12Hash(sourceCommit.hash),
            destHash: short12Hash(destinationCommit.parents![0].hash),
            isLoading: false,
            selectedDiffType: SelectedDiffType.Custom,
          });
          setMultiCommitSelection({
            sourceCommit,
            sourceIndex: specSrcIndex,
            destinationCommit,
            destinationIndex: destIndex,
          });
        } else {
          setIterativeReviewSelectedDiff({
            sourceHash: prAnchorHash,
            destHash: prDestinationHash,
            isLoading: false,
            selectedDiffType: SelectedDiffType.All,
          });
          // Don't strip the permalink hash in case of a bad destination hash
          const urlHash = window.location.hash;
          history.replace(`${diffTabUrl}${urlHash}`);
        }
      } else {
        setIterativeReviewSelectedDiff({
          sourceHash: prAnchorHash,
          destHash: prDestinationHash,
          isLoading: false,
          selectedDiffType: SelectedDiffType.All,
        });
        // Don't strip the permalink hash in case of bad spec hashes, but do strip the bad spec hashes in URL
        const urlHash = window.location.hash;
        history.replace(`${diffTabUrl}${urlHash}`);
      }
    }
    // Don't allow changing the history or the path updatings to rerun the use effect
    // We don't care if the diff type changes to rerun this either.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prAnchorHash,
    prDestinationHash,
    isCommitsLoading,
    setIterativeReviewSelectedDiff,
    diffTabUrl,
    prCommits,
  ]);

  const cslrSetAndAllowed = !!commitCSLRHash && prOpen;
  const cslrEnabled =
    cslrSetAndAllowed && commitCSLRCount > 0 && !mergeCommitFound;

  const refetchIRDiff = useSelector(getPullRequestRefetchIRDiffDummy);

  // handling reload flag cases where PR anchor is updated and we might need to refetch the diff
  useEffect(() => {
    if (refetchIRDiff) {
      // we don't want this to run on the page load/initial render since that is handled by the above useEffect hook
      if (
        iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.None
      ) {
        dispatch({ type: REFETCH_IR_DIFF.FALSE });
        return;
      }
      // since the diff was previously valid, checking if the source commit still exists is sufficient to determine if the dest commit also exists
      // minimalUpdateSaga ensures that commits are re-fetched prior to dispatching refetch diff action so we can assume prCommits are up to date
      const sourceCommitIndex = prCommits.findIndex(
        commit =>
          short12Hash(commit.hash) === iterativeReviewSelectedDiff.sourceHash
      );
      const validSource = sourceCommitIndex > -1;

      // refetch the CSLR checkpoint
      let cslrHash = '';
      const fetchAndSetCommit = async () => {
        cslrHash = await getLastReviewedCommit(
          repositoryOwner,
          repositorySlug,
          pullRequestId
        );
        setCommitCSLRHash(cslrHash);
      };

      fetchAndSetCommit();

      if (
        iterativeReviewSelectedDiff.selectedDiffType ===
          SelectedDiffType.Cslr &&
        cslrEnabled
      ) {
        setIterativeReviewSelectedDiff({
          sourceHash: prAnchorHash,
          destHash: cslrHash,
          isLoading: false,
          selectedDiffType: SelectedDiffType.Cslr,
        });
        history.replace(`${diffTabUrl}/${prAnchorHash}..${cslrHash}`);
      } else if (
        (iterativeReviewSelectedDiff.selectedDiffType ===
          SelectedDiffType.Custom ||
          iterativeReviewSelectedDiff.selectedDiffType ===
            SelectedDiffType.Single) &&
        validSource
      ) {
        // we don't need to reload custom and single commit diffs if they are still valid, but we need to mock the success actions
        // to let minimalUpdateSaga continue onto next steps, fetching conflicts, etc
        dispatch({ type: MOCK_IR_DIFF_FETCH });
      } else {
        setIterativeReviewSelectedDiff({
          sourceHash: prAnchorHash,
          destHash: prDestinationHash,
          isLoading: false,
          selectedDiffType: SelectedDiffType.All,
        });
        history.replace(`${diffTabUrl}`);
        setActiveIndex(-1);
        setMultiCommitSelection(defaultMultiCommitSelection);
      }
      dispatch({ type: REFETCH_IR_DIFF.FALSE });
    }
    // we only want this useEffect to run when prompted via the reload flag/minimal update saga
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchIRDiff]);

  useEffect(() => {
    if (
      iterativeReviewSelectedDiff.selectedDiffType !== SelectedDiffType.None
    ) {
      dispatch(
        publishPullRequestTrackEvent({
          action: 'rendered',
          actionSubject: 'irSelectedDiffType',
          actionSubjectId: '',
          attributes: {
            irTracking,
          },
        })
      );
    }
  }, [dispatch, iterativeReviewSelectedDiff.selectedDiffType, irTracking]);

  // This useEffect only fires when something like Comment Navigation sets the diff because
  // it has no knowledge of all the commits and what to set the highlighting indexes for
  useEffect(
    () => {
      if (
        iterativeReviewSelectedDiff.selectedDiffType ===
          SelectedDiffType.Custom &&
        !multiCommitSelection.sourceCommit
      ) {
        const sourceIndex = prCommits.findIndex(commit =>
          hashCompare(commit.hash, iterativeReviewSelectedDiff.sourceHash)
        );
        // We don't actually want the index of the commit where this is a hash (which might not exist
        // in the case of force pushes anymore), we look for the commit where the parent is the
        // destination hash
        const destinationIndex = prCommits.findIndex(
          commit =>
            commit.parents &&
            shortHash(commit.parents[0].hash) ===
              shortHash(iterativeReviewSelectedDiff.destHash || '')
        );
        if (sourceIndex > -1 && destinationIndex > -1) {
          setMultiCommitSelection({
            sourceCommit: prCommits[sourceIndex],
            sourceIndex,
            destinationCommit: prCommits[destinationIndex],
            destinationIndex,
          });
        }
      }
    }, // we only want this useEffect to run when something changes the diff type
    // and unsets the commit selection source commit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      iterativeReviewSelectedDiff.selectedDiffType,
      multiCommitSelection.sourceCommit,
    ]
  );

  // function to reset the selected commits when a user either closes the
  // IR selector popup or releases shift mid-selection
  const resetToInitialSelection = useCallback(() => {
    if (
      iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.Single
    ) {
      // case to reset to single commit selection
      setMultiCommitSelection(defaultMultiCommitSelection);
    } else {
      // case to reset to multiple commit selection
      setMultiCommitSelection(initialMultiSelectState);
    }
    setActiveIndex(-1);
  }, [
    iterativeReviewSelectedDiff.selectedDiffType,
    initialMultiSelectState,
    setMultiCommitSelection,
    setActiveIndex,
  ]);

  // reset selection when IR selector popup is clicked out of or the
  // dropdown closes when the header transitions to the compact header
  useEffect(() => {
    // We currently render the header (and therefore the IR selector) twice.
    // Once in the main header and once in the sticky header.
    // We need to make sure the hidden IR selector isn't triggering this effect erroneously.
    const isIRSelectorRenderedInStickyHeader =
      isStickyHeaderActive && isStickyHeader;
    const isIRSelectorRenderedInNonStickyHeader =
      !isStickyHeaderActive && !isStickyHeader;
    // this only happens when the user is currently mid-selection (active index >= 0)
    if (
      !isIterativeReviewSelectorOpen &&
      activeIndex >= 0 &&
      (isIRSelectorRenderedInStickyHeader ||
        isIRSelectorRenderedInNonStickyHeader)
    ) {
      resetToInitialSelection();
    }
  }, [
    isIterativeReviewSelectorOpen,
    activeIndex,
    resetToInitialSelection,
    isStickyHeaderActive,
    isStickyHeader,
  ]);

  // This useEffect updates the active commit message which is used to populate the tooltip
  // content. This tooltip is only shown when the user has a single commit selected.
  useEffect(() => {
    if (
      iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.Single
    ) {
      // Show the commit hash and message in the tooltip
      // Example: "#1234567: This is my commit message"
      const hashText = `#${shortHash(iterativeReviewSelectedDiff.sourceHash)}`;
      const activeCommit = prCommits.find(commit =>
        hashCompare(commit.hash, iterativeReviewSelectedDiff.sourceHash)
      );
      const tooltipText = activeCommit
        ? hashText.concat(`: ${getCommitMessage(activeCommit).trim()}`)
        : hashText;
      setTooltipContent(tooltipText);
    } else {
      setTooltipContent('');
    }
  }, [
    iterativeReviewSelectedDiff.selectedDiffType,
    iterativeReviewSelectedDiff.sourceHash,
    prCommits,
  ]);

  const onIRItemSelectEvent = (selectedDropdownItem: string) => {
    publishScreenEvent('irSelectedDropdownItem', {
      irSelectedDropdownItem: selectedDropdownItem,
      prIdentifier: irTracking.prIdentifier,
    });
  };

  const onIRDropdownOpenCloseEvent = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'irDropdownOpenCloseButton',
      actionSubjectId: '',
      source: 'pullRequestDiff',
      attributes: {
        irDropdownStatus: isIterativeReviewSelectorOpen ? 'closed' : 'open',
        prIdentifier: irTracking.prIdentifier,
      },
    });
  };

  const noCslrChanges = cslrSetAndAllowed && commitCSLRCount === 0;

  const content = () => {
    const cslrDescription = () => {
      // We don't want a description label if we encountered a merge commit
      if (cslrEnabled) {
        return (
          <FormattedMessage
            {...messages.changesSinceLastReviewDescription}
            values={{
              commitCount: commitCSLRCount,
              hash: shortHash(commitCSLRHash),
            }}
          />
        );
      } else if (noCslrChanges) {
        return (
          <FormattedMessage {...messages.noChangesSinceLastReviewDescription} />
        );
      } else {
        return undefined;
      }
    };

    const loadMoreCommitsSection = (): JSX.Element | null => {
      if (!nextCommitsUrl) {
        return null;
      }
      if (isCommitsLoading) {
        return (
          <Section aria-labelledby="load-more-commits">
            <Inline alignBlock="center" alignInline="center">
              <Spinner testId="load-more-commits-spinner" />
            </Inline>
          </Section>
        );
      }
      return (
        <Section aria-labelledby="load-more-commits">
          <ButtonItem
            onClick={() => {
              dispatch({ type: prCommitActions.FETCH_MORE_COMMITS });
              onIRItemSelectEvent(prCommitActions.FETCH_MORE_COMMITS);
            }}
          >
            <FormattedMessage {...messages.loadMoreCommits} />
          </ButtonItem>
        </Section>
      );
    };

    return (
      <MenuGroup maxWidth="384px" maxHeight="352px">
        <Section aria-labelledby="quick-select">
          <HeadingItem id="quick-select">
            <FormattedMessage {...messages.quickSelectHeading} />
          </HeadingItem>
          <ButtonItem
            description={
              <FormattedMessage
                {...messages.allChangesDescription}
                values={{ commitCount }}
              />
            }
            onClick={() => {
              history.replace(diffTabUrl);
              setIterativeReviewSelectedDiff({
                sourceHash: prAnchorHash,
                destHash: prDestinationHash,
                isLoading: false,
                selectedDiffType: SelectedDiffType.All,
              });
              setIsIterativeReviewSelectorOpen(false);
              setActiveIndex(-1);
              setMultiCommitSelection(defaultMultiCommitSelection);
              onIRItemSelectEvent(SelectedDiffType[SelectedDiffType.All]);
            }}
            isSelected={
              iterativeReviewSelectedDiff.selectedDiffType ===
                SelectedDiffType.All &&
              multiCommitSelection.sourceCommit === undefined
            }
          >
            <FormattedMessage {...messages.allChanges} />
          </ButtonItem>
          <ButtonItem
            description={cslrDescription()}
            isDisabled={!cslrEnabled}
            onClick={() => {
              history.replace(
                `${diffTabUrl}/${prAnchorHash}..${commitCSLRHash}`
              );
              setIterativeReviewSelectedDiff({
                sourceHash: prAnchorHash,
                destHash: commitCSLRHash,
                isLoading: false,
                selectedDiffType: SelectedDiffType.Cslr,
              });
              setIsIterativeReviewSelectorOpen(false);
              setActiveIndex(-1);
              setMultiCommitSelection(defaultMultiCommitSelection);
              onIRItemSelectEvent(SelectedDiffType[SelectedDiffType.Cslr]);
            }}
            isSelected={
              iterativeReviewSelectedDiff.selectedDiffType ===
                SelectedDiffType.Cslr &&
              multiCommitSelection.sourceCommit === undefined
            }
          >
            <FormattedMessage {...messages.changesSinceLastReview} />
          </ButtonItem>
        </Section>
        <Section aria-labelledby="commits" hasSeparator>
          <HeadingItem id="commits">
            <styles.CommitsHeader>
              <Inline space="space.075">
                <FormattedMessage {...messages.commitsHeading} />
                <styles.ShiftClick>
                  <FormattedMessage {...messages.shiftClick} />
                </styles.ShiftClick>
              </Inline>
            </styles.CommitsHeader>
          </HeadingItem>
          <CommitItemsList
            commits={prCommits}
            closeSelector={() => setIsIterativeReviewSelectorOpen(false)}
            multiCommitSelection={multiCommitSelection}
            setMultiCommitSelection={setMultiCommitSelection}
            setInitialMultiSelectState={setInitialMultiSelectState}
            resetToInitialSelection={resetToInitialSelection}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            onIRItemSelectEvent={onIRItemSelectEvent}
          />
        </Section>
        {loadMoreCommitsSection()}
      </MenuGroup>
    );
  };

  const selectorLabel = () => {
    switch (iterativeReviewSelectedDiff.selectedDiffType) {
      case SelectedDiffType.Single:
        return (
          <FormattedMessage
            {...messages.singleCommitSelectorMessage}
            values={{ hash: shortHash(iterativeReviewSelectedDiff.sourceHash) }}
          />
        );
      case SelectedDiffType.Cslr:
        // check if the review has been completed, if it has then we should show the custom diff message
        // otherwise we should show the changes since last review message
        if (isIterativeReviewCompleted) {
          return (
            <FormattedMessage
              {...messages.customDiff}
              values={{
                srcHash: shortHash(iterativeReviewSelectedDiff.sourceHash),
                destHash: shortHash(iterativeReviewSelectedDiff.destHash!),
              }}
            />
          );
        }
        return <FormattedMessage {...messages.changesSinceLastReview} />;
      case SelectedDiffType.All:
        return <FormattedMessage {...messages.allChanges} />;
      case SelectedDiffType.Custom:
        return (
          <FormattedMessage
            {...messages.customDiff}
            values={{
              srcHash: shortHash(iterativeReviewSelectedDiff.sourceHash),
              destHash: shortHash(iterativeReviewSelectedDiff.destHash!),
            }}
          />
        );
      default:
        return <FormattedMessage {...messages.customDiffFallback} />;
    }
  };

  return (
    <Popup
      id="iterative-review-button"
      isOpen={isIterativeReviewSelectorOpen}
      onClose={() => setIsIterativeReviewSelectorOpen(false)}
      placement="bottom-start"
      content={content}
      shouldFlip={false}
      trigger={triggerProps => (
        <Tooltip
          content={tooltipContent}
          position="bottom"
          hideTooltipOnClick
          canAppear={() =>
            tooltipContent.length > 0 && !isIterativeReviewSelectorOpen
          }
        >
          <LoadingButton
            {...triggerProps}
            appearance={buttonAppearance(isStickyHeader)}
            isLoading={iterativeReviewSelectedDiff.isLoading}
            spacing={buttonSpacing(isStickyHeader)}
            onClick={() => {
              setIsIterativeReviewSelectorOpen(!isIterativeReviewSelectorOpen);
              onIRDropdownOpenCloseEvent();
            }}
            iconAfter={<ChevronDownIcon label="toggle-review-popup" />}
            isSelected={isIterativeReviewSelectorOpen}
            tabIndex={tabIndex}
          >
            {selectorLabel()}
          </LoadingButton>
        </Tooltip>
      )}
    />
  );
};
